import React, { useState, useEffect } from 'react';
import { bookingAPI, eventBookingAPI } from '../../utils/api';
import { CalendarIcon, ClockIcon, UserIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import toast from '../../utils/notifications';

const MyBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchMyBookings();
  }, []);

  const fetchMyBookings = async () => {
    try {
      setLoading(true);
      
      // Fetch accommodation bookings
      const bookingsResponse = await bookingAPI.getMyBookings();
      const paginationData = bookingsResponse.data.data;
      let bookingsData = [];
      
      if (paginationData) {
        bookingsData = paginationData.data || [];
      } else {
        bookingsData = bookingsResponse.data.data || [];
      }
      
      // Fetch event bookings
      const eventResponse = await eventBookingAPI.getMyEventBookings({ per_page: 100 });
      const eventBookingsData = eventResponse.data.data?.data || eventResponse.data.data || [];
      
      // Map event bookings to match accommodation booking structure
      const mappedEventBookings = Array.isArray(eventBookingsData) ? eventBookingsData.map(b => ({
        booking_id: b.event_booking_id,
        service_type: b.event_type,
        booking_time_start: b.event_date,
        booking_time_end: b.event_date,
        party_size: b.number_of_guests,
        total_amount: b.amount_to_pay,
        status: b.status,
        is_event: true,
        event_details: {
          firstName: b.first_name,
          lastName: b.last_name,
          email: b.email,
          phone: b.phone,
          paymentOption: b.payment_option,
          referenceNumber: b.reference_number,
          totalPrice: b.total_price
        }
      })) : [];
      
      // Combine both bookings
      const allBookings = [...(Array.isArray(bookingsData) ? bookingsData : []), ...mappedEventBookings];
      setBookings(allBookings);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId, isEvent = false) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) {
      return;
    }

    try {
      if (isEvent) {
        await eventBookingAPI.updateStatus(bookingId, 'cancelled');
      } else {
        await bookingAPI.delete(bookingId);
      }
      toast.success('Booking cancelled successfully');
      fetchMyBookings();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error('Failed to cancel booking');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: ClockIcon, label: 'Pending' },
      confirmed: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircleIcon, label: 'Confirmed' },
      completed: { bg: 'bg-gray-100', text: 'text-gray-800', icon: CheckCircleIcon, label: 'Completed' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircleIcon, label: 'Cancelled' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-4 h-4 mr-1" />
        {config.label}
      </span>
    );
  };

  const filteredBookings = bookings.filter((booking) => {
    if (filter === 'all') return true;
    return booking.status === filter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Bookings</h1>
          <p className="mt-2 text-sm text-gray-600">View your booking history and manage upcoming reservations</p>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              {['all', 'pending', 'completed', 'cancelled'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`${
                    filter === status
                      ? 'border-amber-500 text-amber-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize`}
                >
                  {status}
                  <span className="ml-2 py-0.5 px-2 rounded-full text-xs bg-gray-100">
                    {bookings.filter(b => status === 'all' || b.status === status).length}
                  </span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Bookings List */}
        {filteredBookings.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-12 text-center">
            <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No bookings found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {filter === 'all' 
                ? "You haven't made any bookings yet." 
                : `You don't have any ${filter} bookings.`}
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredBookings.map((booking) => (
              <div key={booking.booking_id} className="bg-white shadow rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                <div className="p-6">
                  {/* Status Badge */}
                  <div className="flex justify-between items-start mb-4">
                    <span className="text-sm font-medium text-gray-500">
                      {booking.is_event ? 'Event' : 'Accommodation'} #{booking.booking_id}
                    </span>
                    {getStatusBadge(booking.status)}
                  </div>

                  {/* Service Type */}
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {booking.service_type}
                  </h3>

                  <div className="space-y-3">
                    {/* Event or Accommodation Details */}
                    {booking.is_event ? (
                      <>
                        {/* Event Date */}
                        <div className="text-sm">
                          <div className="flex items-start text-gray-600">
                            <CalendarIcon className="h-5 w-5 mr-2 text-gray-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-medium text-gray-700">Event Date</p>
                              <p>{new Date(booking.booking_time_start).toLocaleString('en-PH', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}</p>
                            </div>
                          </div>
                        </div>

                        {/* Guest Details */}
                        <div className="text-sm">
                          <p className="font-medium text-gray-700 mb-1">Guest Information</p>
                          <p className="text-gray-600">{booking.event_details.firstName} {booking.event_details.lastName}</p>
                          <p className="text-gray-600 text-xs">{booking.event_details.email}</p>
                          <p className="text-gray-600 text-xs">{booking.event_details.phone}</p>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Check-in/Check-out */}
                        <div className="text-sm">
                          <div className="flex items-start text-gray-600 mb-1">
                            <CalendarIcon className="h-5 w-5 mr-2 text-gray-400 flex-shrink-0" />
                            <div>
                              <p className="font-medium text-gray-700">Check-in</p>
                              <p>{new Date(booking.booking_time_start).toLocaleString('en-PH', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}</p>
                            </div>
                          </div>
                          <div className="flex items-start text-gray-600 ml-7">
                            <div>
                              <p className="font-medium text-gray-700">Check-out</p>
                              <p>{new Date(booking.booking_time_end).toLocaleString('en-PH', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}</p>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Party Size */}
                    <div className="flex items-center text-sm text-gray-600">
                      <UserIcon className="h-5 w-5 mr-2 text-gray-400" />
                      <span>{booking.party_size} guest{booking.party_size > 1 ? 's' : ''}</span>
                    </div>

                    {/* Order Items (if available) */}
                    {booking.order_items && booking.order_items.length > 0 && (
                      <div className="pt-3 border-t border-gray-200">
                        <p className="text-xs font-medium text-gray-700 mb-2">Order Items:</p>
                        <div className="space-y-1">
                          {booking.order_items.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-xs text-gray-600">
                              <span>{item.quantity}x {item.menu_item?.name || item.name || 'Item'}</span>
                              <span>₱{parseFloat(item.price * item.quantity).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Total Amount */}
                    <div className="pt-3 border-t border-gray-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700">Total Amount</span>
                        <span className="text-lg font-bold text-amber-600">
                          ₱{parseFloat(booking.total_amount).toLocaleString('en-PH', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    </div>

                    {/* Event Payment Option (if event) */}
                    {booking.is_event && booking.event_details.paymentOption && (
                      <div className="pt-3 border-t border-gray-200">
                        <p className="text-xs font-medium text-gray-700">Payment Option:</p>
                        <p className="text-xs text-gray-600">{booking.event_details.paymentOption}</p>
                      </div>
                    )}

                    {/* Special Requests */}
                    {booking.special_requests && (
                      <div className="pt-3 border-t border-gray-200">
                        <p className="text-xs font-medium text-gray-700 mb-1">Special Requests:</p>
                        <p className="text-xs text-gray-600">{booking.special_requests}</p>
                      </div>
                    )}

                    {/* Notes */}
                    {booking.notes && (
                      <div className="pt-3 border-t border-gray-200">
                        <p className="text-xs font-medium text-gray-700 mb-1">Notes:</p>
                        <p className="text-xs text-gray-600">{booking.notes}</p>
                      </div>
                    )}

                    {/* Cancel Button - Only for accommodation bookings */}
                    {booking.status === 'pending' && !booking.is_event && (
                      <div className="pt-3">
                        <button
                          onClick={() => handleCancelBooking(booking.booking_id, booking.is_event)}
                          className="w-full px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors"
                        >
                          Cancel Booking
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyBookings;
